import { useMutation, useQueryClient } from '@tanstack/react-query';
import { emailScheduleService } from '@/services/emailScheduleService';
import type { EmailSchedule, EmailScheduleInput } from '@/types/emailSchedule';
import { queryKeys } from '@/lib/queryKeys';

export const useCreateEmailSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation<EmailSchedule, unknown, EmailScheduleInput>({
    mutationFn: (input) => emailScheduleService.create(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.emailSchedules.list(),
      });
    },
  });
};

export const useUpdateEmailSchedule = () => {
  const queryClient = useQueryClient();
  return useMutation<
    EmailSchedule,
    unknown,
    { id: string; input: Partial<EmailScheduleInput> },
    { previousSchedules: EmailSchedule[] | undefined }
  >({
    mutationFn: ({ id, input }) => emailScheduleService.update(id, input),
    onMutate: async ({ id, input }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.notifications.emailSchedules.list() });
      const previousSchedules = queryClient.getQueryData<EmailSchedule[]>(
        queryKeys.notifications.emailSchedules.list(),
      );
      if (previousSchedules) {
        queryClient.setQueryData<EmailSchedule[]>(
          queryKeys.notifications.emailSchedules.list(),
          (old) =>
            old?.map((schedule) => (schedule.id === id ? { ...schedule, ...input } : schedule)) ??
            [],
        );
      }
      return { previousSchedules };
    },
    onError: (_err, _newTodo, context) => {
      if (context?.previousSchedules) {
        queryClient.setQueryData(
          queryKeys.notifications.emailSchedules.list(),
          context.previousSchedules,
        );
      }
    },
    onSettled: (data) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.emailSchedules.list(),
      });
      if (data) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.notifications.emailSchedules.runs(data.id),
        });
      }
    },
  });
};
